import { useEffect, useRef, useState, type MutableRefObject } from "react";
import * as THREE from "three";

import { useLoomControlSurfaceContext } from "./LoomControlSurfaceContext";
import type { LoomAuthoringDocument } from "./loomAuthoringModel";
import {
  LoomControlSurfaceSceneController,
  resolveLoomControlBodyAtCanvasPoint,
  type LoomControlBodyWorldAnchor,
  type LoomControlSurfaceSceneReceipt,
} from "./loomControlSurfaceViewerScene";

export interface LoomControlSurfaceViewerRuntime {
  camera: THREE.PerspectiveCamera;
  controls?: { addEventListener(type: "change", listener: () => void): void; removeEventListener(type: "change", listener: () => void): void };
  group: THREE.Group;
  invalidatePickTargets?: () => void;
  renderer: THREE.WebGLRenderer;
  requestRender?: () => void;
}

export interface LoomControlSurfaceViewerReceipt extends LoomControlSurfaceSceneReceipt {
  readonly bodyCanvasAnchors: Readonly<Record<string, { readonly x: number; readonly y: number }>>;
}

export function loomControlSurfaceViewerGapPolicy(
  definitionRevision: string | null,
  pose: unknown,
  poseRevision: string | null,
): "hide-topology" | "preserve-topology" | "sync" {
  if (!definitionRevision) return "hide-topology";
  return !pose || !poseRevision ? "preserve-topology" : "sync";
}

export function projectLoomControlBodyCanvasAnchors(
  anchors: readonly LoomControlBodyWorldAnchor[],
  camera: THREE.PerspectiveCamera,
  width: number,
  height: number,
): Readonly<Record<string, { readonly x: number; readonly y: number }>> {
  if (!(width > 0) || !(height > 0)) return {};
  camera.updateMatrixWorld(true);
  return Object.fromEntries(anchors.flatMap(({ bodyId, point }) => {
    const projected = new THREE.Vector3(...point).project(camera);
    if (![projected.x, projected.y, projected.z].every(Number.isFinite)
      || Math.abs(projected.x) > 1 || Math.abs(projected.y) > 1 || Math.abs(projected.z) > 1) return [];
    return [[bodyId, { x: (projected.x + 1) * width / 2, y: (1 - projected.y) * height / 2 }]];
  }));
}

function verifiedBodyCanvasAnchors(
  controller: LoomControlSurfaceSceneController,
  runtime: LoomControlSurfaceViewerRuntime,
): Readonly<Record<string, { readonly x: number; readonly y: number }>> {
  const bounds = runtime.renderer.domElement.getBoundingClientRect();
  const centers = controller.controlBodyWorldAnchors();
  const candidates = new Map(controller.controlBodyWorldPickCandidates().map(({ bodyId, points }) => [bodyId, points]));
  const verified: Record<string, { x: number; y: number }> = {};
  for (const center of centers) {
    const points = [center.point, ...(candidates.get(center.bodyId) ?? [])];
    for (const point of points) {
      const projected = projectLoomControlBodyCanvasAnchors(
        [{ bodyId: center.bodyId, point }], runtime.camera, bounds.width, bounds.height,
      )[center.bodyId];
      if (projected && resolveLoomControlBodyAtCanvasPoint(
        runtime.group, runtime.camera, projected.x, projected.y, bounds.width, bounds.height,
      ) === center.bodyId) { verified[center.bodyId] = projected; break; }
    }
  }
  return verified;
}

export function useLoomControlSurfaceViewerScene(
  runtimeRef: MutableRefObject<LoomControlSurfaceViewerRuntime | null>,
  document: LoomAuthoringDocument,
): LoomControlSurfaceViewerReceipt | null {
  const context = useLoomControlSurfaceContext();
  const controllerRef = useRef<{ host: THREE.Group; controller: LoomControlSurfaceSceneController } | null>(null);
  const [receipt, setReceipt] = useState<LoomControlSurfaceViewerReceipt | null>(null);

  useEffect(() => {
    const runtime = runtimeRef.current;
    const collection = context?.collection; const pose = context?.pose;
    const geometry = context?.acceptedGeometry;
    const definitionRevision = context?.definitionRevision; const poseRevision = context?.poseRevision;
    const wingNode = collection ? document.nodes.find(({ id }) => id === collection.wingId) : null;
    if (!runtime || !collection || !geometry
        || wingNode?.type !== "primitive" || wingNode.definition.primitive !== "wing") {
      controllerRef.current?.controller.dispose(); controllerRef.current = null; setReceipt((current) => current === null ? current : null);
      runtime?.requestRender?.(); return;
    }
    if (controllerRef.current?.host !== runtime.group) {
      controllerRef.current?.controller.dispose();
      controllerRef.current = { host: runtime.group, controller: new LoomControlSurfaceSceneController(runtime.group) };
    }
    const gapPolicy = loomControlSurfaceViewerGapPolicy(definitionRevision ?? null, pose, poseRevision ?? null);
    if (gapPolicy === "hide-topology") {
      controllerRef.current.controller.dispose();
      setReceipt((current) => current === null ? current : null);
      runtime.requestRender?.(); return;
    }
    if (gapPolicy === "preserve-topology") {
      setReceipt((current) => current === null ? current : null);
      runtime.requestRender?.(); return;
    }
    if (!pose || !definitionRevision || !poseRevision) return;
    try {
      const sceneReceipt = controllerRef.current.controller.sync({
        collection, geometry, pose, poseRevision,
        selectedBodyId: context?.selectedBodyId ?? null,
      });
      const next: LoomControlSurfaceViewerReceipt = {
        ...sceneReceipt,
        bodyCanvasAnchors: verifiedBodyCanvasAnchors(controllerRef.current.controller, runtime),
      };
      setReceipt((current) => current
        && current.topologyBuildCount === next.topologyBuildCount
        && current.poseApplyCount === next.poseApplyCount
        && current.fixedWingBuildCount === next.fixedWingBuildCount
        && current.fixedBodyBufferSignature === next.fixedBodyBufferSignature
        && current.fixedBodyObjectSignature === next.fixedBodyObjectSignature
        && current.fixedBodyTransformSignature === next.fixedBodyTransformSignature
        && JSON.stringify(current.bodyCanvasAnchors) === JSON.stringify(next.bodyCanvasAnchors)
        ? current : next);
      context?.reportViewerReady();
      runtime.invalidatePickTargets?.(); runtime.requestRender?.();
    } catch (reason) {
      if (!controllerRef.current.controller.hasAcceptedLayer()) {
        controllerRef.current.controller.dispose(); setReceipt(null);
      }
      runtime.requestRender?.();
      context?.reportViewerError(reason instanceof Error ? reason.message : "scene evaluation failed");
    }
  });

  useEffect(() => {
    const runtime = runtimeRef.current; const entry = controllerRef.current;
    if (!runtime || !entry || !receipt) return;
    const publish = () => {
      const bodyCanvasAnchors = verifiedBodyCanvasAnchors(entry.controller, runtime);
      setReceipt((current) => !current || JSON.stringify(current.bodyCanvasAnchors) === JSON.stringify(bodyCanvasAnchors)
        ? current : { ...current, bodyCanvasAnchors });
    };
    runtime.controls?.addEventListener("change", publish);
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(publish);
    observer?.observe(runtime.renderer.domElement); publish();
    return () => { runtime.controls?.removeEventListener("change", publish); observer?.disconnect(); };
  }, [receipt?.poseApplyCount, receipt?.topologyBuildCount, runtimeRef]);

  useEffect(() => {
    const runtime = runtimeRef.current; const selectBody = context?.selectBody;
    if (!runtime || !selectBody) return;
    const canvas = runtime.renderer.domElement; let start: { x: number; y: number } | null = null;
    const down = (event: PointerEvent) => { start = { x: event.clientX, y: event.clientY }; };
    const up = (event: PointerEvent) => {
      const initial = start; start = null;
      if (!initial || Math.hypot(event.clientX - initial.x, event.clientY - initial.y) > 5) return;
      const bounds = canvas.getBoundingClientRect();
      const bodyId = resolveLoomControlBodyAtCanvasPoint(
        runtime.group, runtime.camera, event.clientX - bounds.left, event.clientY - bounds.top,
        bounds.width, bounds.height,
      );
      if (bodyId) selectBody(bodyId);
    };
    canvas.addEventListener("pointerdown", down); canvas.addEventListener("pointerup", up);
    return () => { canvas.removeEventListener("pointerdown", down); canvas.removeEventListener("pointerup", up); };
  }, [context?.selectBody, receipt, runtimeRef]);

  useEffect(() => () => controllerRef.current?.controller.dispose(), []);
  return receipt;
}
